import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { defineMessages, useIntl } from 'react-intl';
import type { VisualDxComparePayload } from '../../contracts';
import {
  VisualDxDiagnosisCards,
  type VisualDxDiagnosisCardItem,
} from './VisualDxDiagnosisCards';

/**
 * Heidi-native VisualDx side-by-side comparator (PRD Module 4). Mounted by
 * ``InteractiveWidgetBlock`` for the ``visualdx_compare`` kind when the LLM
 * calls ``compare_ddx_view`` and the BE acks with the parsed payload.
 *
 * The grid is ``VisualDxDiagnosisCards`` — the same tile the diagnostic flow
 * already shows for ``enrich_diagnoses_with_links`` — with the synopsis
 * passed through as the card description. That keeps every VisualDx
 * diagnosis tile in the chat one shape. ``compare_url`` becomes the cards'
 * "Compare all on VisualDx" link.
 */

const messages = defineMessages({
  empty: {
    id: 'NvCmzYXaVo',
    defaultMessage: 'No comparison data available.',
    description: 'Empty state when the side-by-side comparison has no diagnoses',
  },
  heading: {
    id: '81xo73yE6t',
    defaultMessage: 'Side-by-side comparison',
    description: 'Heading above the compared diagnoses',
  },
  count: {
    id: 'WJVuAUu2NG',
    defaultMessage: '{count, plural, one {# diagnosis} other {# diagnoses}}',
    description: 'Number of diagnoses in the comparison',
  },
  disclaimer: {
    id: 'uOZFjYibfL',
    defaultMessage: 'Reference information, not a diagnosis. Images © VisualDx.',
    description: 'Footer under the comparison grid',
  },
});

interface VisualDxCompareProps {
  compare: VisualDxComparePayload;
}

const containerClass =
  'flex w-full flex-col gap-3 rounded-xl bg-background-secondary p-4 text-text-primary';

const headerRowClass = 'flex items-baseline justify-between gap-2';

export const VisualDxCompare = ({ compare }: VisualDxCompareProps): JSX.Element => {
  const intl = useIntl();
  const diagnoses = compare.diagnoses ?? [];

  const items: VisualDxDiagnosisCardItem[] = diagnoses.map((dx) => ({
    name: dx.name,
    thumbnail_url: dx.image_url,
    visualdx_url: dx.visualdx_url,
    description: dx.synopsis,
  }));

  if (items.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.empty)}
        </TypographyCaption>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={headerRowClass}>
        <TypographyP2Bold className="text-text-primary">
          {intl.formatMessage(messages.heading)}
        </TypographyP2Bold>
        <TypographyCaption className="shrink-0 text-text-secondary">
          {intl.formatMessage(messages.count, { count: items.length })}
        </TypographyCaption>
      </div>

      <VisualDxDiagnosisCards diagnoses={items} compare_url={compare.compare_url} />

      <TypographyCaption className="text-text-tertiary">
        {intl.formatMessage(messages.disclaimer)}
      </TypographyCaption>
    </div>
  );
};
